// La sesion en la cabecera del `AppShell` (HU-32a · MG-ILE-63, «Vacío 2: cabecera con varios roles»).
//
// QUE PASABA (reportado por el operador, 2-oct-2026). La cabecera pintaba un `RoleChip` y su ambito
// POR CADA ROL. Con el actor simulado de desarrollo (los ocho roles) salian ocho chips en tres filas,
// la cabecera crecia con los roles y el nombre del sistema quedaba cortado en «Farm…».
//
// AHORA, en una sola linea: `texto_de_la_cabecera` («Nombre · rol principal», tal cual lo arma el
// servidor) y, si la persona tiene mas de un rol, el indicador «+N roles». La lista completa de roles
// con su ambito vive en un panel que abre el avatar («Mis roles y países»), junto con «Cerrar
// sesión». A 390 px el texto se oculta y queda el avatar con el mismo panel: los roles se pueden
// consultar en cualquier ancho.
//
// Es un DESPLEGABLE (patron «disclosure» de WAI-ARIA), no un cuadro: no atrapa el foco. El boton
// lleva `aria-expanded` y `aria-controls`; Escape lo cierra y devuelve el foco al boton; el clic
// fuera y salir de el con el tabulador tambien lo cierran.
//
// LA PILA DE ESCAPE (HU-51, tarea 51-4). «Cerrar sesión» abre el cuadro de confirmacion ENCIMA del
// panel, que sigue abierto debajo: Escape cierra primero el cuadro (el foco vuelve a «Cerrar
// sesión», dentro del panel) y solo la siguiente pulsacion cierra el panel. Mientras haya una capa
// abierta (`hayModalAbierto`, el contador de cuadros y listas de `ui.tsx`) el panel no atiende
// Escape ni el clic fuera: esa capa esta encima, porque con el telon de un cuadro delante el avatar
// no se puede pulsar. Y cuando el panel atiende Escape, la tecla no sigue hasta el atajo del
// `AppShell` que pliega la barra lateral.
import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, LogOut } from 'lucide-react';
import { Button, RoleChip, StatusBadge, SUPERFICIE, hayModalAbierto } from './ui';
import { tonoDelRol } from '../data/navegacion';
import { TEXTO_USUARIO_EXTERNO } from '../lib/etiquetas';
import type { FocusEvent } from 'react';
import type { SesionDelServidor } from '../api/sesion';

export interface PropsDeMenuDeSesion {
  /** La sesion del servidor: el texto de la cabecera, los roles con su ambito y si es externo. */
  sesion: SesionDelServidor;
  /** El nombre que se usa si el servidor no lo trae (la entrada simulada de desarrollo). */
  nombreDeReserva: string;
  /** La inicial que se usa si no hay nombre del que sacarla. */
  inicialDeReserva: string;
  /** «Cerrar sesión»: abre el cuadro de confirmacion del `AppShell`. */
  onCerrarSesion: () => void;
}

/** El nombre accesible del boton del avatar y el titulo del panel (spec MG-ILE-63). */
export const TEXTO_MIS_ROLES = 'Mis roles y países';

/** «+1 rol», «+7 roles»: cuantos roles hay ademas del que nombra la cabecera. */
export const textoDeRolesAdicionales = (adicionales: number): string =>
  `+${adicionales} ${adicionales === 1 ? 'rol' : 'roles'}`;

export default function MenuDeSesion({ sesion, nombreDeReserva, inicialDeReserva, onCerrarSesion }: PropsDeMenuDeSesion) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const idDelPanel = useId();
  const idDelTitulo = useId();

  const nombre = sesion.usuario || nombreDeReserva;
  const inicial = nombre.trim().charAt(0).toUpperCase() || inicialDeReserva;
  const adicionales = Math.max(0, sesion.roles.length - 1);

  // El boton se busca dentro de la caja: el `Button` del catalogo no reenvia `ref`.
  const enfocarElBoton = () => caja.current?.querySelector<HTMLButtonElement>('[data-disparador]')?.focus();

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e: MouseEvent) => {
      if (hayModalAbierto()) return;
      const destino = e.target as Node | null;
      if (destino && caja.current?.contains(destino)) return;
      setAbierto(false);
    };
    // En CAPTURA, como el `Modal`: asi la tecla llega aunque el foco este en un control que se la
    // trague, y detenerla aqui evita que el atajo del `AppShell` pliegue ademas la barra lateral.
    const teclas = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || hayModalAbierto()) return;
      e.stopPropagation();
      setAbierto(false);
      enfocarElBoton();
    };
    document.addEventListener('mousedown', fuera);
    window.addEventListener('keydown', teclas, true);
    return () => {
      document.removeEventListener('mousedown', fuera);
      window.removeEventListener('keydown', teclas, true);
    };
  }, [abierto]);

  // Salir del panel con el tabulador lo cierra (sin trampa de foco). Si el foco se fue a un cuadro
  // abierto desde el panel («Cerrar sesión»), el panel se queda: el cuadro le devolvera el foco.
  const alSalir = (e: FocusEvent<HTMLDivElement>) => {
    const destino = e.relatedTarget as Node | null;
    if (!destino || caja.current?.contains(destino) || hayModalAbierto()) return;
    setAbierto(false);
  };

  return (
    <div ref={caja} onBlur={alSalir} className="relative ml-auto flex min-w-0 items-center gap-3">
      {/* El bloque de sesion cede espacio, no el nombre del sistema: el texto se recorta (con el
          completo en `title`) y el indicador de roles no. Por debajo de 640 px se oculta. */}
      <div className="flex min-w-0 items-center gap-2 max-sm:hidden">
        <p data-testid="sesion-usuario" title={sesion.texto_de_la_cabecera}
          className="min-w-0 truncate text-body font-semibold text-ink">
          {sesion.texto_de_la_cabecera}
        </p>
        {adicionales > 0 && (
          <span data-testid="sesion-roles-adicionales"
            className="shrink-0 whitespace-nowrap rounded-full border border-borde bg-canvas px-2 py-0.5 text-meta font-semibold text-ink-soft">
            {textoDeRolesAdicionales(adicionales)}
          </span>
        )}
      </div>

      <Button variant="secondary" size="sm" data-disparador="" data-testid="sesion-menu-abrir"
        aria-label={TEXTO_MIS_ROLES} title={TEXTO_MIS_ROLES}
        aria-expanded={abierto} aria-controls={idDelPanel}
        onClick={() => setAbierto((v) => !v)}
        className="shrink-0">
        <span aria-hidden="true"
          className="flex h-7 w-7 items-center justify-center rounded-full bg-marca-profundo font-mono text-micro font-bold text-white">
          {inicial}
        </span>
        <ChevronDown size={14} aria-hidden="true"
          className={'text-ink-soft transition-transform ' + (abierto ? 'rotate-180' : '')} />
      </Button>

      {abierto && (
        <div id={idDelPanel} data-testid="sesion-menu" role="group" aria-labelledby={idDelTitulo}
          className={SUPERFICIE + ' absolute right-0 top-full z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] p-4'}>
          <p id={idDelTitulo} className="text-meta font-semibold text-ink-soft">
            {TEXTO_MIS_ROLES}
          </p>
          <p className="mt-2 break-words text-body font-semibold text-ink">{nombre}</p>
          {sesion.correo && <p className="break-all text-meta text-ink-soft">{sesion.correo}</p>}
          {sesion.es_externo && (
            <p className="mt-2">
              <StatusBadge data-testid="sesion-externo" tone="info">{TEXTO_USUARIO_EXTERNO}</StatusBadge>
            </p>
          )}

          {/* Un rol por fila con SU ambito debajo (RN-60: el ambito es por rol, no de la persona).
              Apilados y no lado a lado: con un rol largo («Comité de Farmacovigilancia») el ambito
              saltaba de linea en unas filas y en otras no. */}
          <ul className="mt-3 flex flex-col gap-2.5 border-t border-hairline pt-3">
            {sesion.roles.map((r) => (
              <li key={r.rol} className="flex flex-col items-start gap-1">
                <RoleChip data-testid="sesion-rol" tone={tonoDelRol(r.rol)}>{r.texto_del_rol}</RoleChip>
                <span data-testid="sesion-ambito" className="text-meta text-ink-soft">{r.texto_del_ambito}</span>
              </li>
            ))}
          </ul>

          <div className="mt-3 border-t border-hairline pt-3">
            <Button variant="secondary" size="sm" icon={LogOut} data-testid="sesion-salir"
              onClick={onCerrarSesion} className="w-full">
              Cerrar sesión
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
